import { useEffect, useState } from "react";
import { api, formatINR } from "@/lib/api";
import { toast, Toaster } from "sonner";
import {
  Send, Layers, Building2, Search, X, CheckSquare, Square, Banknote,
  Eye, Download, CheckCircle2, PauseCircle, Play, ShieldCheck, AlertTriangle,
} from "lucide-react";
import { RefreshMastersButton } from "@/components/RefreshMastersButton";
import { PaginationControls } from "@/components/PaginationControls";
import { useMastersRefresh } from "@/hooks/useMastersRefresh";
import UserLabel, { formatUser, useUserDirectory } from "@/components/UserLabel";
import BeneficiaryVerificationDialog from "@/components/BeneficiaryVerificationDialog";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

function fmtDate(d) {
  if (!d) return "—";
  try { return new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return d; }
}

export default function PostPaymentPage() {
  // Sprint 2026-Feb-10n — hydrate the {email → name} directory so
  // `posted_by` / `approved_by` render as the user's display name.
  useUserDirectory();
  const mastersRefreshKey = useMastersRefresh();
  const [vouchers, setVouchers] = useState([]);
  const [advances, setAdvances] = useState([]);
  const [batches, setBatches] = useState([]);
  const [banks, setBanks] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [search, setSearch] = useState("");
  const [batchSearch, setBatchSearch] = useState("");
  const [batchReconFilter, setBatchReconFilter] = useState("ALL");
  const [batchStatusFilter, setBatchStatusFilter] = useState("ALL");
  const [batchPage, setBatchPage] = useState(1);
  const [batchPageSize, setBatchPageSize] = useState(50);
  const [tab, setTab] = useState("post");
  const [posting, setPosting] = useState(false);
  const [batchDate, setBatchDate] = useState(new Date().toISOString().slice(0, 10));
  const [bankId, setBankId] = useState("");
  const [notes, setNotes] = useState("");
  const [viewingBatch, setViewingBatch] = useState(null);
  // "Mark as Paid" modal — captures the actual payment date (separate from
  // the system-recorded paid_at timestamp) plus optional remarks (UTR /
  // cheque no etc). Replacing the legacy window.prompt so we can persist
  // the real cash-out date for audit + downstream paid-date display.
  const [markPaidFor, setMarkPaidFor] = useState(null);
  const [markPaidDate, setMarkPaidDate] = useState(new Date().toISOString().slice(0, 10));
  const [markPaidRemarks, setMarkPaidRemarks] = useState("");
  const [markPaidBusy, setMarkPaidBusy] = useState(false);
  // Sprint 2026-Mar-01 — Payment Integrity: verify beneficiaries
  // dialog. Opened by clicking the ShieldCheck button on a batch row
  // OR automatically before every "Download Excel". The dialog fetches
  // /api/batch-payments/{bid}/beneficiary-verification and disables
  // the Download button until every row reports status="ok".
  const [verifyForBatch, setVerifyForBatch] = useState(null);
  // 2026-Feb-05 UX — "Select Top N" convenience for large queues.
  // Purely a selection helper; touches no business logic. Reads
  // whatever list is currently visible (respects tab + filters +
  // sorting) and ticks the first N ids into `selected`.
  const [topN, setTopN] = useState("");

  // 2026-Jul-16 fix — server lists are paginated (default 200, max 500).
  // The Post-Payment queue must show EVERY approved item (a single salary
  // voucher can fast-track 900+ BPVs), so fetch all pages.
  const fetchAllPages = async (url, params = {}) => {
    const PAGE = 500;
    let skip = 0;
    const all = [];
    for (;;) {
      const r = await api.get(url, { params: { ...params, skip, limit: PAGE } });
      const chunk = Array.isArray(r.data) ? r.data : (r.data?.items || []);
      all.push(...chunk);
      const total = Number(r.headers?.["x-total-count"]);
      if (chunk.length < PAGE || (total && all.length >= total) || skip > 20000) break;
      skip += PAGE;
    }
    return all;
  };

  const loadVouchers = () => fetchAllPages("/bill-payment-vouchers", { status: "Approved" })
    .then(list => setVouchers(list.filter(v => Number(v.total_net_payable || 0) > 0)));
  // Approved vendor advances that are NOT yet inserted into a payment batch.
  const loadAdvances = () => fetchAllPages("/vendor-advances", { status: "Approved" })
    .then(list => setAdvances(list.filter(a => !a.posted_batch_id && Number(a.advance_amount || 0) > 0)));
  // Jun-2026 fix — /batch-payments is paginated (default 200); fetch ALL
  // pages so older batches never disappear, then paginate client-side.
  const loadBatches = () => fetchAllPages("/batch-payments").then(setBatches);
  const loadBanks = () => api.get("/bank-ledgers", { params: { active_only: true } }).then(r => setBanks(r.data || []));
  useEffect(() => { loadVouchers(); loadAdvances(); loadBatches(); loadBanks(); }, [mastersRefreshKey]);

  // Split Approved vouchers into the active queue (Post tab) and held queue (Hold tab).
  const liveVouchers = vouchers.filter(v => !v.payment_hold);
  const heldVouchers = vouchers.filter(v => !!v.payment_hold);
  // Same split for advances.
  const liveAdvances = advances.filter(a => !a.payment_hold);
  const heldAdvances = advances.filter(a => !!a.payment_hold);

  // Unified Post-Payment list: BPVs + Approved Advances together, each tagged
  // with `_kind` so the table can render a Module column and the post action
  // can split selected IDs by type when calling /batch-payments.
  const taggedBPVs = liveVouchers.map(v => ({
    ...v, _kind: "bpv", _module_label: "Bill Payment",
    _document_number: v.voucher_number, _amount: Number(v.total_net_payable || 0),
  }));
  const taggedAdvances = liveAdvances.map(a => ({
    ...a, _kind: "advance", _module_label: "Vendor Advance",
    _document_number: a.advance_number, _amount: Number(a.advance_amount || 0),
    branch_name: a.branch_name || "",
  }));
  const liveItems = [...taggedBPVs, ...taggedAdvances];
  // Unified Held list — BPVs + Advances on hold.
  const heldItems = [
    ...heldVouchers.map(v => ({
      ...v, _kind: "bpv", _module_label: "Bill Payment",
      _document_number: v.voucher_number, _amount: Number(v.total_net_payable || 0),
    })),
    ...heldAdvances.map(a => ({
      ...a, _kind: "advance", _module_label: "Vendor Advance",
      _document_number: a.advance_number, _amount: Number(a.advance_amount || 0),
      branch_name: a.branch_name || "",
    })),
  ];

  const filtered = liveItems.filter(v => {
    if (!search.trim()) return true;
    const q = search.trim().toLowerCase();
    return [v._document_number, v.vendor_name, v.notes, v.purpose, v._module_label]
      .some(s => (s || "").toString().toLowerCase().includes(q));
  });
  const filteredHeld = heldItems.filter(v => {
    if (!search.trim()) return true;
    const q = search.trim().toLowerCase();
    return [v._document_number, v.vendor_name, v.notes, v.payment_hold_reason, v._module_label]
      .some(s => (s || "").toString().toLowerCase().includes(q));
  });
  const filteredBatches = batches.filter(b => {
    if (batchReconFilter !== "ALL" && (b.reconciliation_status || "Not Reconciled") !== batchReconFilter) return false;
    if (batchStatusFilter !== "ALL") {
      const status = b.paid_at ? "Paid" : "Posted";
      if (status !== batchStatusFilter) return false;
    }
    if (!batchSearch.trim()) return true;
    const q = batchSearch.trim().toLowerCase();
    // Sprint 10n — also search on the resolved user *name* so users
    // can find batches by "Rajesh" as well as by the stored email.
    return [b.batch_number, b.bank_ledger_name, b.notes, b.posted_by, formatUser(b.posted_by, ""), b.fy]
      .some(s => (s || "").toString().toLowerCase().includes(q));
  });
  // Client-side pagination for the (now complete) batches list.
  const pagedBatches = filteredBatches.slice((batchPage - 1) * batchPageSize, batchPage * batchPageSize);
  useEffect(() => { setBatchPage(1); }, [batchSearch, batchReconFilter, batchStatusFilter, batchPageSize]);

  // Tab-aware: on the Hold tab we work against the held list, otherwise
  // against the unified Post-Payment list (BPVs + Approved Advances).
  const activeList = tab === "hold" ? filteredHeld : filtered;
  const allSelected = activeList.length > 0 && activeList.every(v => selected.has(v.id));
  function toggleAll() {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(activeList.map(v => v.id)));
  }
  function toggle(id) {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  }

  // "Select Top N" helper — ticks the first N ids of the CURRENT
  // `activeList` (which already reflects tab, search, sort). Empty /
  // 0 / non-numeric input is rejected; N > activeList.length is
  // clamped with a clear toast so the operator knows why the number
  // shrank. Uses a Set for O(N) selection — fine for the 10k-row case
  // the enhancement was scoped for.
  function applyTopN() {
    const n = parseInt(topN, 10);
    if (!Number.isFinite(n) || n <= 0) {
      toast.error("Enter a positive number of rows to select.");
      return;
    }
    if (activeList.length === 0) {
      toast.error("No rows available in the current view to select.");
      return;
    }
    if (n > activeList.length) {
      toast.error(`Only ${activeList.length} row(s) available in the current view — pick a smaller number.`);
      return;
    }
    const picked = new Set();
    for (let i = 0; i < n; i++) picked.add(activeList[i].id);
    setSelected(picked);
    toast.success(`Selected top ${n} row(s).`);
  }

  const selVouchers = activeList.filter(v => selected.has(v.id));
  const totalSelected = selVouchers.reduce((s, v) => s + Number(
    v._amount !== undefined ? v._amount : (v.total_net_payable || 0)
  ), 0);
  // 2026-Feb-05 bug fix — count vendors by identity (vendor_id), not
  // display name. Prior code (`v.vendor_name`) silently merged two
  // distinct vendors sharing the same display string (e.g. two
  // employees both named "Kishan Nayak", vendor_codes RCPL1144 /
  // RCPL1956) into a single vendor slot, so 144 vouchers appeared
  // as "143 vendors". `vendor_id` is the authoritative identity;
  // `vendor_name` remains a fallback for legacy BPVs that were
  // created before the salary fast-track fix started stamping
  // vendor_id on the header — for those rows the old (imprecise)
  // count is at least preserved, never worsened.
  const vendorsSelected = new Set(
    selVouchers
      .map(v => v.vendor_id || v.vendor_name)
      .filter(Boolean),
  ).size;

  // Clear selection whenever the user switches tabs (otherwise IDs from one tab
  // would invisibly bleed into actions on another tab).
  useEffect(() => { setSelected(new Set()); setTopN(""); }, [tab]);

  async function holdSelected() {
    if (selected.size === 0) { toast.error("Select at least one item to hold"); return; }
    if (bankId) { toast.error("Uncheck the Source Bank before holding — Hold and Post are mutually exclusive."); return; }
    // Split selection by kind so each goes to the right endpoint.
    const bpvIds = [];
    const advIds = [];
    for (const item of activeList) {
      if (!selected.has(item.id)) continue;
      if (item._kind === "advance") advIds.push(item.id);
      else bpvIds.push(item.id);
    }
    const reason = window.prompt(
      `Hold ${selected.size} item(s) totalling ${formatINR(totalSelected)}?\n\n` +
      `Held items move to the "Hold Post Payments" tab and stay out of the Post-Payment queue ` +
      `until you release them.\n\nReason (optional):`,
      ""
    );
    if (reason === null) return;
    try {
      let heldCount = 0;
      let skipCount = 0;
      if (bpvIds.length) {
        const r = await api.post("/bill-payment-vouchers/hold", { ids: bpvIds, reason: reason || "" });
        heldCount += (r.data?.held || []).length;
        skipCount += (r.data?.skipped || []).length;
      }
      if (advIds.length) {
        const r = await api.post("/vendor-advances/hold", { ids: advIds, reason: reason || "" });
        heldCount += (r.data?.held || []).length;
        skipCount += (r.data?.skipped || []).length;
      }
      toast.success(`${heldCount} item(s) moved to Hold${skipCount ? ` · ${skipCount} skipped` : ""}`);
      setSelected(new Set());
      loadVouchers(); loadAdvances();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Hold failed");
    }
  }

  async function releaseAndPost() {
    if (selected.size === 0) { toast.error("Select at least one held item to release"); return; }
    if (!bankId) { toast.error("Select the source Bank Ledger before releasing — released items post directly to a batch"); return; }
    const bank = banks.find(b => b.id === bankId);
    const bankLbl = bank ? `${bank.bank_name} — ${bank.account_number}` : "selected bank";
    const bpvIds = [];
    const advIds = [];
    for (const item of activeList) {
      if (!selected.has(item.id)) continue;
      if (item._kind === "advance") advIds.push(item.id);
      else bpvIds.push(item.id);
    }
    if (!window.confirm(
      `Release ${selected.size} held item(s) totalling ${formatINR(totalSelected)} and post them as a batch from ${bankLbl}?\n\n` +
      `Held flags will be cleared and the items will move to the Batches tab as a single Paid batch.`
    )) return;
    setPosting(true);
    try {
      // 1) Release the hold so batch endpoint will accept them.
      if (bpvIds.length) await api.post("/bill-payment-vouchers/release-hold", { ids: bpvIds });
      if (advIds.length) await api.post("/vendor-advances/release-hold", { ids: advIds });
      // 2) Create the batch — items immediately move to Posted status.
      const r = await api.post("/batch-payments", {
        bill_payment_voucher_ids: bpvIds,
        vendor_advance_ids: advIds,
        bank_ledger_id: bankId,
        batch_date: batchDate,
        notes,
      });
      toast.success(`Released & posted — Batch ${r.data.batch_number} created (${r.data.total_vendors} vendors, ${formatINR(r.data.total_amount)})`);
      setSelected(new Set()); setNotes("");
      loadVouchers(); loadAdvances(); loadBatches();
      setTab("batches");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Release & Post failed");
      loadVouchers(); loadAdvances();
    } finally { setPosting(false); }
  }

  async function postPayment() {
    if (selected.size === 0) { toast.error("Select at least one approved voucher or advance"); return; }
    if (!bankId) { toast.error("Select the source Bank Ledger before posting"); return; }
    const bank = banks.find(b => b.id === bankId);
    const bankLbl = bank ? `${bank.bank_name} — ${bank.account_number}` : "selected bank";
    // Split selection into BPV vs Advance IDs by looking each id up in the active list.
    const bpvIds = [];
    const advIds = [];
    for (const item of activeList) {
      if (!selected.has(item.id)) continue;
      if (item._kind === "advance") advIds.push(item.id);
      else bpvIds.push(item.id);
    }
    if (!window.confirm(`Post ${selected.size} item(s) totalling ${formatINR(totalSelected)} from ${bankLbl}? They will move into a new payment batch.`)) return;
    setPosting(true);
    try {
      const r = await api.post("/batch-payments", {
        bill_payment_voucher_ids: bpvIds,
        vendor_advance_ids: advIds,
        bank_ledger_id: bankId,
        batch_date: batchDate,
        notes,
      });
      toast.success(`Batch ${r.data.batch_number} created — ${r.data.total_vendors} vendors, ${formatINR(r.data.total_amount)}`);
      setSelected(new Set()); setNotes("");
      loadVouchers(); loadAdvances(); loadBatches();
      setTab("batches");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Posting failed");
    } finally { setPosting(false); }
  }

  async function downloadBatchExcel(b) {
    // Sprint 2026-Mar-01 — Payment Integrity: force the operator
    // through the Verify Beneficiaries dialog before any Excel bytes
    // ever leave the server. The dialog owns the actual download
    // call, and enables the Download button only when every row is
    // status="ok". This prevents accidental wrong-beneficiary
    // payments in the same shape as the Sudhir-Pradhan / HDFC0001107
    // incident on 2026-Mar-04.
    setVerifyForBatch(b);
  }

  async function markPaid(b) {
    // Open the date+remarks modal. Default the date to today so the
    // common case is a single click; user can backdate if needed.
    setMarkPaidFor(b);
    setMarkPaidDate(new Date().toISOString().slice(0, 10));
    setMarkPaidRemarks("");
  }

  async function confirmMarkPaid() {
    if (!markPaidFor || !markPaidDate) return;
    setMarkPaidBusy(true);
    const b = markPaidFor;
    try {
      await api.post(`/batch-payments/${b.id}/mark-paid`, {
        actual_payment_date: markPaidDate,
        remarks: markPaidRemarks || "",
      });
      toast.success(`Batch ${b.batch_number} marked Paid on ${fmtDate(markPaidDate)}`);
      setMarkPaidFor(null);
      loadBatches(); loadVouchers();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to mark paid");
    } finally {
      setMarkPaidBusy(false);
    }
  }

  return (
    <div className="py-2 w-full">
      <Toaster richColors />
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="font-display text-3xl font-bold flex items-center gap-2"><Send size={24} className="text-[#F97316]"/> Post Payment</h1>
          <div className="text-xs text-slate-500 mt-1">Select fully approved Bill Payment Vouchers, choose source bank, and post the batch.</div>
        </div>
        <RefreshMastersButton testid="pp-refresh-masters-btn" />
      </div>

      <div className="flex gap-1 mb-4 border-b border-slate-200">
        {[
          ["post", `Post Payment (${liveItems.length})`],
          ["hold", `Hold Post Payments (${heldItems.length})`],
          ["batches", `Batches (${batches.length})`],
        ].map(([k, l]) => (
          <button key={k} data-testid={`pp-tab-${k}`} onClick={() => setTab(k)}
            className={`px-5 py-2.5 text-xs uppercase tracking-wider font-bold border-b-2 -mb-px ${
              tab === k ? "border-[#F97316] text-slate-900" : "border-transparent text-slate-500 hover:text-slate-800"
            }`}>{l}</button>
        ))}
      </div>

      {tab === "post" && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-3 mb-4 bg-slate-50 border border-slate-200 px-4 py-3">
            <div className="md:col-span-4">
              <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1 flex items-center gap-1"><Banknote size={11}/> Source Bank *</div>
              <select value={bankId} onChange={e => setBankId(e.target.value)} data-testid="pp-bank-select"
                className="w-full text-xs border border-slate-300 px-2 py-1.5 focus:border-[#F97316] focus:outline-none bg-white">
                <option value="">— Select bank —</option>
                {banks.map(b => <option key={b.id} value={b.id}>{b.bank_name} — {b.account_number}</option>)}
              </select>
              {banks.length === 0 && <div className="text-[10px] text-rose-600 mt-1">No active banks. Add one under Masters → Bank Ledger.</div>}
            </div>
            <div className="md:col-span-2">
              <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">Batch Date</div>
              <input type="date" value={batchDate} onChange={e => setBatchDate(e.target.value)} data-testid="pp-batch-date"
                className="w-full text-xs border border-slate-300 px-2 py-1.5 focus:border-[#F97316] focus:outline-none"/>
            </div>
            <div className="md:col-span-3">
              <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">Notes</div>
              <input value={notes} onChange={e => setNotes(e.target.value)} placeholder="e.g. April vendor payments"
                className="w-full text-xs border border-slate-300 px-2 py-1.5 focus:border-[#F97316] focus:outline-none"/>
            </div>
            <div className="md:col-span-3 flex flex-col justify-end">
              <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Selected</div>
              <div className="font-bold tabular text-xs">
                <span data-testid="pp-selected-count">{selected.size}</span> vouchers · <span data-testid="pp-selected-vendors">{vendorsSelected}</span> vendors
              </div>
              <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mt-1">Total Net Payable</div>
              <div className="font-bold text-base text-emerald-700 tabular" data-testid="pp-total-amount">{formatINR(totalSelected)}</div>
            </div>
          </div>
          <div className="flex justify-end gap-2 mb-3">
            <button onClick={holdSelected} disabled={selected.size === 0 || !!bankId}
              data-testid="pp-hold-btn"
              title={bankId
                ? "Hold is disabled while a Source Bank is selected. Clear the bank to enable Hold."
                : "Move selected items to Hold — they stay out of the Post-Payment queue until released."}
              className="inline-flex items-center gap-2 bg-white border border-amber-500 text-amber-700 hover:bg-amber-50 px-4 py-2.5 text-xs font-bold uppercase tracking-wider disabled:opacity-50">
              <PauseCircle size={14}/> Hold ({selected.size})
            </button>
            <button onClick={postPayment} disabled={posting || selected.size === 0 || !bankId}
              data-testid="pp-post-btn"
              className="inline-flex items-center gap-2 bg-[#F97316] text-white px-5 py-2.5 text-xs font-bold uppercase tracking-wider hover:bg-[#EA580C] disabled:opacity-50">
              <Send size={14}/> {posting ? "Posting…" : `Post Payment (${selected.size})`}
            </button>
          </div>

          <div className="flex items-center gap-3 mb-3 flex-wrap">
            <div className="relative flex-1 max-w-md">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
              <input value={search} onChange={e => setSearch(e.target.value)} data-testid="pp-search"
                placeholder="Search voucher #, vendor…"
                className="w-full pl-9 pr-8 py-2 text-xs border border-slate-300 focus:border-[#F97316] focus:outline-none bg-white"/>
              {search && <button onClick={() => setSearch("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"><X size={13}/></button>}
            </div>
            {/* 2026-Feb-05 — Select Top N convenience. Selection-only,
                no business-logic impact. Respects current filters + sort. */}
            <div className="flex items-center gap-1.5 border border-slate-300 bg-white pl-2.5 pr-1 py-1"
                 title="Tick the first N rows currently visible in this list (respects search / sort).">
              <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Select Top</span>
              <input
                type="number"
                min={1}
                max={activeList.length || undefined}
                value={topN}
                onChange={e => setTopN(e.target.value)}
                onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); applyTopN(); } }}
                data-testid="pp-topn-input"
                placeholder="N"
                className="w-16 text-xs px-1.5 py-1 border border-slate-200 focus:border-[#F97316] focus:outline-none tabular text-right"
              />
              <button
                onClick={applyTopN}
                data-testid="pp-topn-apply"
                disabled={!topN || activeList.length === 0}
                className="inline-flex items-center gap-1 bg-slate-800 hover:bg-slate-700 text-white text-[10px] uppercase tracking-wider font-bold px-2.5 py-1 disabled:opacity-40 disabled:cursor-not-allowed">
                Apply
              </button>
            </div>
            <div className="text-[11px] uppercase tracking-wider font-bold text-slate-500 ml-auto" data-testid="pp-visible-count">
              {filtered.length} approved vouchers
            </div>
          </div>

          <div className="border border-slate-200 bg-white overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 border-b-2 border-slate-200">
                <tr>
                  <th className="w-10 py-3 px-2">
                    <button onClick={toggleAll} className="text-slate-500 hover:text-[#F97316]" data-testid="pp-select-all">
                      {allSelected ? <CheckSquare size={15}/> : <Square size={15}/>}
                    </button>
                  </th>
                  {["Voucher #", "Module", "Date", "Vendor", "Bills", "Branch", "Approved By", "Net Payable"].map(h =>
                    <th key={h} className="text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider py-3 px-2">{h}</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 && (
                  <tr><td colSpan={9} className="py-10 text-center text-slate-400">No approved vouchers or advances ready for posting.</td></tr>
                )}
                {filtered.map(v => (
                  <tr key={v.id} className={`border-b border-slate-100 cursor-pointer hover:bg-slate-50/60 ${selected.has(v.id) ? "bg-orange-50/40" : ""}`}
                    onClick={() => toggle(v.id)}>
                    <td className="py-2 px-2">
                      <button data-testid={`pp-select-${v.id}`} onClick={e => { e.stopPropagation(); toggle(v.id); }} className="text-slate-500 hover:text-[#F97316]">
                        {selected.has(v.id) ? <CheckSquare size={14} className="text-[#F97316]"/> : <Square size={14}/>}
                      </button>
                    </td>
                    <td className="py-2 px-2 font-bold text-slate-900">{v._document_number}</td>
                    <td className="py-2 px-2">
                      <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 ${
                        v._kind === "advance"
                          ? "bg-amber-100 text-amber-800"
                          : "bg-indigo-100 text-indigo-800"
                      }`} data-testid={`pp-module-${v.id}`}>{v._module_label}</span>
                    </td>
                    <td className="py-2 px-2 text-slate-600">{fmtDate(v.voucher_date || v.advance_date)}</td>
                    <td className="py-2 px-2 text-slate-700">{v.vendor_name}</td>
                    <td className="py-2 px-2 tabular text-slate-600">{v._kind === "advance" ? "—" : (v.lines || []).length}</td>
                    <td className="py-2 px-2 text-slate-600">{v.branch_name ? <span className="inline-flex items-center gap-1 text-[10px]"><Building2 size={9}/> {v.branch_name}</span> : "—"}</td>
                    <td className="py-2 px-2 text-slate-500 text-[10px]" title={v.approved_by || ""}>
                      <UserLabel value={v.approved_by} fallback="—" testid={`pp-live-approved-by-${v.id}`} />
                    </td>
                    <td className="py-2 px-2 text-right tabular font-bold text-emerald-700">{formatINR(v._amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {tab === "hold" && (
        <>
          <div className="bg-amber-50 border border-amber-300 px-4 py-3 mb-3 flex items-start gap-3">
            <PauseCircle size={18} className="text-amber-600 shrink-0 mt-0.5"/>
            <div className="text-[11px] text-amber-900 leading-snug">
              These Approved vouchers are <strong>on hold</strong>. Pick a Source Bank below, select the held vouchers,
              then click <strong>Release &amp; Post</strong> — they will be released from hold and immediately moved into a
              new Paid Batch on the Batches tab.
            </div>
          </div>

          {/* Bank / Batch Date / Notes — mirrors the Post Payment tab so Release & Post can create a batch in one click */}
          <div className="grid grid-cols-1 md:grid-cols-12 gap-3 mb-4 bg-amber-50/40 border border-amber-200 px-4 py-3">
            <div className="md:col-span-4">
              <div className="text-[10px] uppercase tracking-wider font-bold text-amber-800 mb-1 flex items-center gap-1"><Banknote size={11}/> Source Bank *</div>
              <select value={bankId} onChange={e => setBankId(e.target.value)} data-testid="pp-hold-bank-select"
                className="w-full text-xs border border-amber-300 px-2 py-1.5 focus:border-amber-600 focus:outline-none bg-white">
                <option value="">— Select bank —</option>
                {banks.map(b => <option key={b.id} value={b.id}>{b.bank_name} — {b.account_number}</option>)}
              </select>
              {banks.length === 0 && <div className="text-[10px] text-rose-600 mt-1">No active banks. Add one under Masters → Bank Ledger.</div>}
            </div>
            <div className="md:col-span-2">
              <div className="text-[10px] uppercase tracking-wider font-bold text-amber-800 mb-1">Batch Date</div>
              <input type="date" value={batchDate} onChange={e => setBatchDate(e.target.value)} data-testid="pp-hold-batch-date"
                className="w-full text-xs border border-amber-300 px-2 py-1.5 focus:border-amber-600 focus:outline-none"/>
            </div>
            <div className="md:col-span-3">
              <div className="text-[10px] uppercase tracking-wider font-bold text-amber-800 mb-1">Notes</div>
              <input value={notes} onChange={e => setNotes(e.target.value)} placeholder="e.g. April held vendor payments"
                className="w-full text-xs border border-amber-300 px-2 py-1.5 focus:border-amber-600 focus:outline-none"/>
            </div>
            <div className="md:col-span-3 flex flex-col justify-end">
              <div className="text-[10px] uppercase tracking-wider font-bold text-amber-800">Selected</div>
              <div className="font-bold tabular text-xs">
                <span data-testid="pp-hold-selected-count">{selected.size}</span> vouchers · <span data-testid="pp-hold-selected-vendors">{vendorsSelected}</span> vendors
              </div>
              <div className="text-[10px] uppercase tracking-wider font-bold text-amber-800 mt-1">Total Net Payable</div>
              <div className="font-bold text-base text-amber-900 tabular" data-testid="pp-hold-total-amount">{formatINR(totalSelected)}</div>
            </div>
          </div>

          <div className="flex justify-end mb-3">
            <button onClick={releaseAndPost} disabled={posting || selected.size === 0 || !bankId}
              data-testid="pp-release-btn"
              title="Releases the held vouchers AND creates a Paid Batch on the Batches tab in one step."
              className="inline-flex items-center gap-2 bg-emerald-600 text-white px-5 py-2.5 text-xs font-bold uppercase tracking-wider hover:bg-emerald-700 disabled:opacity-50">
              <Play size={14}/> {posting ? "Releasing & Posting…" : `Release & Post (${selected.size})`}
            </button>
          </div>

          <div className="flex items-center gap-3 mb-3">
            <div className="relative flex-1 max-w-md">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
              <input value={search} onChange={e => setSearch(e.target.value)} data-testid="pp-hold-search"
                placeholder="Search voucher #, vendor, hold reason…"
                className="w-full pl-9 pr-8 py-2 text-xs border border-slate-300 focus:border-[#F97316] focus:outline-none bg-white"/>
              {search && <button onClick={() => setSearch("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"><X size={13}/></button>}
            </div>
            <div className="text-[11px] uppercase tracking-wider font-bold text-slate-500 ml-auto">
              {filteredHeld.length} held vouchers
            </div>
          </div>

          <div className="border border-slate-200 bg-white overflow-x-auto" data-testid="pp-held-table-wrap">
            <table className="w-full text-xs">
              <thead className="bg-amber-50/50 border-b-2 border-amber-200">
                <tr>
                  <th className="w-10 py-3 px-2">
                    <button onClick={toggleAll} className="text-amber-700 hover:text-amber-900" data-testid="pp-held-select-all">
                      {allSelected ? <CheckSquare size={15}/> : <Square size={15}/>}
                    </button>
                  </th>
                  {["Voucher #", "Module", "Date", "Vendor", "Branch", "Held By", "Held On", "Reason", "Net Payable"].map(h =>
                    <th key={h} className="text-left text-[10px] font-bold text-amber-800 uppercase tracking-wider py-3 px-2">{h}</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {filteredHeld.length === 0 && (
                  <tr><td colSpan={10} className="py-10 text-center text-slate-400">No items on hold. Use the Post Payment tab → &quot;Hold&quot; to park items here.</td></tr>
                )}
                {filteredHeld.map(v => (
                  <tr key={v.id} className={`border-b border-slate-100 cursor-pointer hover:bg-amber-50/30 ${selected.has(v.id) ? "bg-amber-100/60" : ""}`}
                    onClick={() => toggle(v.id)}>
                    <td className="py-2 px-2">
                      <button data-testid={`pp-held-select-${v.id}`} onClick={e => { e.stopPropagation(); toggle(v.id); }} className="text-slate-500 hover:text-amber-700">
                        {selected.has(v.id) ? <CheckSquare size={14} className="text-amber-700"/> : <Square size={14}/>}
                      </button>
                    </td>
                    <td className="py-2 px-2 font-bold text-slate-900">{v._document_number}</td>
                    <td className="py-2 px-2">
                      <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 ${
                        v._kind === "advance" ? "bg-amber-100 text-amber-800" : "bg-indigo-100 text-indigo-800"
                      }`} data-testid={`pp-held-module-${v.id}`}>{v._module_label}</span>
                    </td>
                    <td className="py-2 px-2 text-slate-600">{fmtDate(v.voucher_date || v.advance_date)}</td>
                    <td className="py-2 px-2 text-slate-700">{v.vendor_name}</td>
                    <td className="py-2 px-2 text-slate-600">{v.branch_name ? <span className="inline-flex items-center gap-1 text-[10px]"><Building2 size={9}/> {v.branch_name}</span> : "—"}</td>
                    <td className="py-2 px-2 text-slate-500 text-[10px]">{v.payment_hold_by || "—"}</td>
                    <td className="py-2 px-2 text-slate-500 text-[10px]">{fmtDate(v.payment_hold_at)}</td>
                    <td className="py-2 px-2 text-slate-700 text-[11px] max-w-[260px]" title={v.payment_hold_reason || ""}>
                      <span className="line-clamp-2">{v.payment_hold_reason || <span className="text-slate-400">—</span>}</span>
                    </td>
                    <td className="py-2 px-2 text-right tabular font-bold text-amber-800">{formatINR(v._amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {tab === "batches" && (
        <>
          <div className="flex items-center gap-3 mb-3">
            <div className="relative flex-1 max-w-sm">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
              <input value={batchSearch} onChange={e => setBatchSearch(e.target.value)} data-testid="pp-batch-search"
                placeholder="Search batch #, bank, FY, notes…"
                className="w-full pl-9 pr-8 py-2 text-xs border border-slate-300 focus:border-[#F97316] focus:outline-none bg-white"/>
              {batchSearch && <button onClick={() => setBatchSearch("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"><X size={13}/></button>}
            </div>
            <select value={batchReconFilter} onChange={e => setBatchReconFilter(e.target.value)} data-testid="pp-batch-recon-filter"
              className="text-xs border border-slate-300 px-2 py-2 bg-white focus:border-[#F97316] focus:outline-none">
              <option value="ALL">All Recon Status</option>
              <option value="Matched">Matched</option>
              <option value="Partially Reconciled">Partially Reconciled</option>
              <option value="Difference">Difference</option>
              <option value="Not Reconciled">Not Reconciled</option>
            </select>
            <select value={batchStatusFilter} onChange={e => setBatchStatusFilter(e.target.value)} data-testid="pp-batch-status-filter"
              className="text-xs border border-slate-300 px-2 py-2 bg-white focus:border-[#F97316] focus:outline-none">
              <option value="ALL">All Status</option>
              <option value="Posted">Posted</option>
              <option value="Paid">Paid</option>
            </select>
            {(batchReconFilter !== "ALL" || batchStatusFilter !== "ALL" || batchSearch) && (
              <button onClick={() => { setBatchReconFilter("ALL"); setBatchStatusFilter("ALL"); setBatchSearch(""); }}
                data-testid="pp-batch-clear-filters"
                className="text-[10px] uppercase font-bold tracking-wider text-slate-500 hover:text-slate-800">
                Clear filters
              </button>
            )}
            <div className="text-[11px] uppercase tracking-wider font-bold text-slate-500 ml-auto">
              {filteredBatches.length} batches
            </div>
          </div>
          <div className="border border-slate-200 bg-white overflow-hidden" data-testid="pp-batches-table-wrap">
            <table className="w-full text-xs table-fixed">
              {/* Fixed column widths — batch grid must fit a single viewport width.
                  Long text columns truncate with a tooltip on hover. */}
              <colgroup>
                <col className="w-[10%]" />{/* Batch No */}
                <col className="w-[6%]"  />{/* Date */}
                <col className="w-[8%]"  />{/* Bank */}
                <col className="w-[7%]"  />{/* Posted By */}
                <col className="w-[4%]"  />{/* Vendors */}
                <col className="w-[4%]"  />{/* Vouchers */}
                <col className="w-[9%]"  />{/* Total */}
                <col className="w-[8%]"  />{/* Bank Recon */}
                <col className="w-[9%]"  />{/* Difference */}
                <col className="w-[9%]"  />{/* Recon Status */}
                <col className="w-[4%]"  />{/* FY */}
                <col className="w-[5%]"  />{/* Status */}
                <col className="w-[7%]"  />{/* Paid Date */}
                <col className="w-[11%]" />{/* Actions */}
              </colgroup>
              <thead className="bg-slate-50 border-b-2 border-slate-200">
                <tr>{["Batch No", "Date", "Bank", "Posted By", "Vendors", "Vouchers", "Total Paid Amount", "Bank Recon", "Difference", "Recon Status", "FY", "Status", "Paid Date", "Actions"].map(h =>
                  <th key={h} className="text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider py-2 px-1.5 leading-tight align-bottom">{h}</th>
                )}</tr>
              </thead>
              <tbody>
                {filteredBatches.length === 0 && <tr><td colSpan={14} className="py-10 text-center text-slate-400">No batches found.</td></tr>}
                {pagedBatches.map(b => {
                  const diff = Number(b.reconciliation_difference || 0);
                  const status = b.reconciliation_status || "Not Reconciled";
                  const statusClass = status === "Matched" ? "bg-emerald-100 text-emerald-800"
                    : status === "Partially Reconciled" ? "bg-amber-100 text-amber-800"
                    : status === "Difference" ? "bg-rose-100 text-rose-800"
                    : "bg-slate-100 text-slate-600";
                  const voucherCount = (b.bill_payment_voucher_ids || []).length + (b.vendor_advance_ids || []).length;
                  return (
                  <tr key={b.id} className="border-b border-slate-100 hover:bg-slate-50/60">
                    <td className="py-2 px-1.5 font-bold text-slate-900 truncate" title={b.batch_number}>
                      <span className="inline-flex items-center gap-1 min-w-0">
                        <Layers size={11} className="text-[#F97316] shrink-0"/>
                        <span className="truncate">{b.batch_number}</span>
                      </span>
                    </td>
                    <td className="py-2 px-1.5 text-slate-600 truncate" title={fmtDate(b.batch_date)}>{fmtDate(b.batch_date)}</td>
                    <td className="py-2 px-1.5 text-slate-700 truncate" title={b.bank_ledger_name || ""}>{b.bank_ledger_name || "—"}</td>
                    <td className="py-2 px-1.5 text-slate-600 truncate" title={b.posted_by}>
                      <UserLabel value={b.posted_by} fallback="—" testid={`pp-batch-posted-by-${b.id}`} />
                    </td>
                    <td className="py-2 px-1.5 tabular text-right" title={String(b.total_vendors)}>{b.total_vendors}</td>
                    <td className="py-2 px-1.5 tabular text-right" title={String(voucherCount)}>{voucherCount}</td>
                    <td className="py-2 px-1.5 text-right tabular font-bold text-emerald-700 truncate" title={formatINR(b.total_amount)}>{formatINR(b.total_amount)}</td>
                    <td className="py-2 px-1.5 text-right tabular truncate" data-testid={`pp-recon-amt-${b.id}`}
                        title={b.reconciliation_count > 0 ? formatINR(b.reconciliation_amount) : "—"}>
                      {b.reconciliation_count > 0 ? formatINR(b.reconciliation_amount) : <span className="text-slate-400">—</span>}
                    </td>
                    <td className={`py-2 px-1.5 text-right tabular font-bold truncate ${diff === 0 ? "text-slate-400" : diff > 0 ? "text-amber-700" : "text-rose-700"}`}
                        title={b.reconciliation_count > 0 ? formatINR(diff) : "—"}>
                      {b.reconciliation_count > 0 ? formatINR(diff) : <span className="text-slate-400">—</span>}
                    </td>
                    <td className="py-2 px-1.5 overflow-hidden" data-testid={`pp-recon-status-${b.id}`}>
                      <span
                        className={`inline-block max-w-full text-[9px] font-bold uppercase tracking-wider px-1 py-0.5 truncate ${statusClass}`}
                        title={status}
                      >
                        {status}
                      </span>
                    </td>
                    <td className="py-2 px-1.5 text-slate-600 truncate" title={b.fy || ""}>{b.fy}</td>
                    <td className="py-2 px-1.5 overflow-hidden">
                      {b.paid_at
                        ? <span className="inline-block text-[9px] font-bold uppercase tracking-wider bg-emerald-100 text-emerald-800 px-1 py-0.5" title="Paid">Paid</span>
                        : <span className="inline-block text-[9px] font-bold uppercase tracking-wider bg-indigo-100 text-indigo-800 px-1 py-0.5" title="Posted">Posted</span>}
                    </td>
                    <td className="py-2 px-1.5 truncate text-slate-700"
                        data-testid={`pp-paid-date-${b.id}`}
                        title={b.actual_payment_date ? fmtDate(b.actual_payment_date) : "—"}>
                      {b.actual_payment_date ? fmtDate(b.actual_payment_date) : <span className="text-slate-400">—</span>}
                    </td>
                    <td className="py-2 px-1.5 whitespace-nowrap">
                      <div className="flex items-center gap-1">
                        <button onClick={() => setViewingBatch(b)} data-testid={`pp-view-batch-${b.id}`}
                          title="View"
                          className="inline-flex items-center justify-center border border-slate-300 text-slate-700 w-6 h-6 hover:bg-slate-100">
                          <Eye size={12}/>
                        </button>
                        <button onClick={() => setVerifyForBatch(b)} data-testid={`pp-verify-batch-${b.id}`}
                          title="Verify Beneficiaries"
                          className="inline-flex items-center justify-center border border-slate-300 text-slate-700 w-6 h-6 hover:bg-emerald-50">
                          <ShieldCheck size={12}/>
                        </button>
                        <button onClick={() => downloadBatchExcel(b)} data-testid={`pp-export-batch-${b.id}`}
                          title="Download Excel (verifies beneficiaries first)"
                          className="inline-flex items-center justify-center border border-slate-300 text-slate-700 w-6 h-6 hover:bg-slate-100">
                          <Download size={12}/>
                        </button>
                        {!b.paid_at && (
                          <button onClick={() => markPaid(b)} data-testid={`pp-mark-paid-${b.id}`}
                            title="Mark as Paid"
                            className="inline-flex items-center justify-center bg-emerald-600 text-white w-6 h-6 hover:bg-emerald-700">
                            <CheckCircle2 size={12}/>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );})}
              </tbody>
            </table>
          </div>
          <div className="mt-2" data-testid="pp-batches-pagination">
            <PaginationControls page={batchPage} pageSize={batchPageSize} total={filteredBatches.length}
              onPage={setBatchPage} onPageSize={(n) => { setBatchPage(1); setBatchPageSize(n); }}
              pageSizeOptions={[25, 50, 100, 200]} />
          </div>
        </>
      )}

      {viewingBatch && <BatchDetailModal batch={viewingBatch} onClose={() => setViewingBatch(null)}/>}
      {verifyForBatch && (
        <BeneficiaryVerificationDialog
          batch={verifyForBatch}
          onClose={() => setVerifyForBatch(null)}
        />
      )}

      {/* Mark as Paid — captures actual cash-out date + remarks */}
      {markPaidFor && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
             onClick={() => !markPaidBusy && setMarkPaidFor(null)}
             data-testid="mark-paid-modal">
          <div className="bg-white max-w-md w-full p-5 border-t-4 border-emerald-600"
               onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-display text-lg font-black text-emerald-700 flex items-center gap-2">
                <CheckCircle2 size={18}/> Mark Batch Paid
              </h3>
              <button onClick={() => !markPaidBusy && setMarkPaidFor(null)} disabled={markPaidBusy}>
                <X size={16}/>
              </button>
            </div>
            <div className="text-xs text-slate-700 mb-3">
              Batch <strong>{markPaidFor.batch_number}</strong> · {formatINR(markPaidFor.total_amount)}
              <br/>
              {(markPaidFor.bill_payment_voucher_ids || []).length + (markPaidFor.vendor_advance_ids || []).length} item(s) will be marked Paid.
            </div>
            <label className="block mb-3">
              <span className="text-[10px] font-bold uppercase tracking-wider text-slate-600 block mb-1">Actual Payment Date *</span>
              <input type="date" value={markPaidDate}
                     onChange={e => setMarkPaidDate(e.target.value)}
                     max={new Date().toISOString().slice(0, 10)}
                     data-testid="mark-paid-date"
                     className="w-full text-sm px-3 py-2 border border-slate-300 focus:border-emerald-600 focus:outline-none"/>
              <span className="text-[10px] text-slate-500 mt-1 block">
                The real date funds left the bank (UTR settlement date / cheque clearing date). Stored separately from the system timestamp.
              </span>
            </label>
            <label className="block">
              <span className="text-[10px] font-bold uppercase tracking-wider text-slate-600 block mb-1">Remarks (UTR / cheque no — optional)</span>
              <textarea rows={2} value={markPaidRemarks}
                        onChange={e => setMarkPaidRemarks(e.target.value)}
                        data-testid="mark-paid-remarks"
                        placeholder="e.g. UTR HDFC0123456789"
                        className="w-full text-sm px-3 py-2 border border-slate-300 focus:border-emerald-600 focus:outline-none"/>
            </label>
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setMarkPaidFor(null)} disabled={markPaidBusy}
                      className="px-3 py-2 border border-slate-300 text-xs uppercase tracking-wider font-bold"
                      data-testid="mark-paid-cancel">Cancel</button>
              <button onClick={confirmMarkPaid} disabled={markPaidBusy || !markPaidDate}
                      data-testid="mark-paid-submit"
                      className="bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-300 disabled:cursor-not-allowed text-white px-4 py-2 text-xs font-bold uppercase tracking-wider inline-flex items-center gap-1">
                <CheckCircle2 size={11}/> {markPaidBusy ? "Marking…" : "Mark Paid"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function BatchDetailModal({ batch, onClose }) {
  const [full, setFull] = useState(null);
  useEffect(() => {
    api.get(`/batch-payments/${batch.id}`).then(r => setFull(r.data)).catch(() => setFull(batch));
  }, [batch.id]);
  const data = full || batch;
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-3 overflow-y-auto">
      <div className="bg-white border border-slate-200 shadow-xl w-full max-w-[1300px] max-h-[95vh] flex flex-col">
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between bg-slate-50">
          <div>
            <h2 className="font-display text-xl font-bold flex items-center gap-2"><Layers size={20} className="text-[#F97316]"/> {data.batch_number}</h2>
            <div className="text-[11px] text-slate-500 mt-0.5 flex items-center gap-3 flex-wrap">
              <span>{fmtDate(data.batch_date)}</span>
              <span className="text-indigo-700">Bank: {data.bank_ledger_name || "—"}</span>
              <span>Posted by <UserLabel value={data.posted_by} fallback="—" testid="pp-batch-detail-posted-by" /></span>
              <span>FY {data.fy}</span>
              {data.paid_at
                ? <span className="text-[10px] font-bold uppercase tracking-wider bg-emerald-100 text-emerald-800 px-1.5 py-0.5">Paid · {data.paid_at.slice(0,10)}</span>
                : <span className="text-[10px] font-bold uppercase tracking-wider bg-indigo-100 text-indigo-800 px-1.5 py-0.5">Posted</span>}
            </div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="pp-batch-detail-close"><X size={20}/></button>
        </div>
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          <div className="grid grid-cols-4 gap-3">
            <Kpi label="Vendors" value={data.total_vendors}/>
            <Kpi label="Items" value={(data.bill_payment_voucher_ids || []).length + (data.vendor_advance_ids || []).length}/>
            <Kpi label="Total Amount" value={formatINR(data.total_amount)} accent/>
            <Kpi label="Notes" value={data.notes || "—"}/>
          </div>
          <div className="border border-slate-200 overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 border-b-2 border-slate-200">
                <tr>{["Voucher #", "Module", "Vendor", "Bills", "Invoice", "GST", "TDS", "Deductions", "Holds", "Net Payable", "Status"].map(h =>
                  <th key={h} className="text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider py-3 px-2">{h}</th>
                )}</tr>
              </thead>
              <tbody>
                {(data.vouchers || []).map(v => (
                  <tr key={v.id} className="border-b border-slate-100">
                    <td className="py-2 px-2 font-bold text-slate-900">{v.voucher_number}</td>
                    <td className="py-2 px-2"><span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 bg-indigo-100 text-indigo-800">Bill Payment</span></td>
                    <td className="py-2 px-2 text-slate-700">{v.vendor_name}</td>
                    <td className="py-2 px-2 tabular text-slate-600">{(v.lines || []).length}</td>
                    <td className="py-2 px-2 text-right tabular">{formatINR(v.total_invoice)}</td>
                    <td className="py-2 px-2 text-right tabular">{formatINR(v.total_gst)}</td>
                    <td className="py-2 px-2 text-right tabular text-rose-600">{formatINR(v.total_tds)}</td>
                    <td className="py-2 px-2 text-right tabular text-orange-700">{v.total_deductions > 0 ? formatINR(v.total_deductions) : "—"}</td>
                    <td className="py-2 px-2 text-right tabular text-amber-700">{v.total_holds > 0 ? formatINR(v.total_holds) : "—"}</td>
                    <td className="py-2 px-2 text-right tabular font-bold text-emerald-700">{formatINR(v.total_net_payable)}</td>
                    <td className="py-2 px-2">
                      <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 ${v.status === "Paid" ? "bg-emerald-100 text-emerald-800" : "bg-indigo-100 text-indigo-800"}`}>{v.status}</span>
                    </td>
                  </tr>
                ))}
                {(data.advances || []).map(a => (
                  <tr key={a.id} className="border-b border-slate-100 bg-amber-50/30">
                    <td className="py-2 px-2 font-bold text-slate-900">{a.advance_number}</td>
                    <td className="py-2 px-2"><span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 bg-amber-100 text-amber-800">Vendor Advance</span></td>
                    <td className="py-2 px-2 text-slate-700">{a.vendor_name}</td>
                    <td className="py-2 px-2 tabular text-slate-400" title={a.purpose || ""}>—</td>
                    <td className="py-2 px-2 text-right tabular text-slate-400">—</td>
                    <td className="py-2 px-2 text-right tabular text-slate-400">—</td>
                    <td className="py-2 px-2 text-right tabular text-slate-400">—</td>
                    <td className="py-2 px-2 text-right tabular text-slate-400">—</td>
                    <td className="py-2 px-2 text-right tabular text-slate-400">—</td>
                    <td className="py-2 px-2 text-right tabular font-bold text-emerald-700">{formatINR(a.advance_amount)}</td>
                    <td className="py-2 px-2">
                      <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 ${a.status === "Paid" ? "bg-emerald-100 text-emerald-800" : "bg-indigo-100 text-indigo-800"}`}>{a.status}</span>
                    </td>
                  </tr>
                ))}
                {(data.vouchers || []).length === 0 && (data.advances || []).length === 0 && <tr><td colSpan={11} className="py-6 text-center text-slate-400">Loading…</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

function Kpi({ label, value, accent }) {
  return (
    <div className={`border ${accent ? "border-[#F97316] bg-orange-50" : "border-slate-200 bg-white"} px-3 py-2`}>
      <div className="text-[9px] uppercase tracking-wider font-bold text-slate-500">{label}</div>
      <div className={`text-xs font-bold tabular ${accent ? "text-[#F97316]" : "text-slate-900"}`}>{value}</div>
    </div>
  );
}
